/**
 * Route Details Page
 * View and manage individual route, including stops and executions
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, ArrowLeft, Play, Pause, X, UserPlus, Check, Search, Truck, MapPin, Clock, CheckCircle2, SkipForward, RefreshCw, Package } from 'lucide-react';
import { transportApi, type RawMaterialRoute, type DriverDTO, type RouteExecution, type StopLog } from '../../services/transport/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

const statusColors: Record<string, { bg: string; text: string }> = {
  DRAFT: { bg: 'bg-gray-100', text: 'text-gray-800' },
  ACTIVE: { bg: 'bg-green-100', text: 'text-green-800' },
  PAUSED: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  COMPLETED: { bg: 'bg-blue-100', text: 'text-blue-800' },
  CANCELLED: { bg: 'bg-red-100', text: 'text-red-800' }
};

export default function RouteDetailsPage() {
  const { routeId } = useParams<{ routeId: string }>();
  const navigate = useNavigate();
  const [route, setRoute] = useState<RawMaterialRoute | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Today's execution state
  const [todayExecution, setTodayExecution] = useState<RouteExecution | null>(null);
  const [stopLogs, setStopLogs] = useState<StopLog[]>([]);
  const [executionLoading, setExecutionLoading] = useState(false);

  // Assign Driver dialog state
  const [isDriverDialogOpen, setIsDriverDialogOpen] = useState(false);
  const [drivers, setDrivers] = useState<DriverDTO[]>([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<DriverDTO | null>(null);
  const [driverSearchQuery, setDriverSearchQuery] = useState('');
  const [driverValidationError, setDriverValidationError] = useState<string | null>(null);
  const [assigningDriver, setAssigningDriver] = useState(false);


  useEffect(() => {
    if (!routeId) {
      navigate('/transport/raw-material/routes');
      return;
    }
    fetchRoute();
    fetchTodayExecution();
  }, [routeId]);

  // Fetch today's execution and stop logs
  const fetchTodayExecution = async () => {
    if (!routeId) return;

    try {
      setExecutionLoading(true);
      const execution = await transportApi.getTodayExecutionByRouteId(parseInt(routeId));
      setTodayExecution(execution);

      if (execution?.id) {
        const logs = await transportApi.getExecutionStops(execution.id);
        setStopLogs(logs);
      } else {
        setStopLogs([]);
      }
    } catch (err) {
      console.error('Failed to fetch today execution:', err);
    } finally {
      setExecutionLoading(false);
    }
  };

  const fetchRoute = async () => {
    try {
      setLoading(true);
      const data = await transportApi.getRouteDetails(parseInt(routeId!));
      setRoute(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load route');
    } finally {
      setLoading(false);
    }
  };

  const fetchDrivers = useCallback(async () => {
    setDriversLoading(true);
    try {
      const driverList = await transportApi.getDrivers();

      // Collect unique vehicleIds from drivers that have a mapped vehicle
      const vehicleIds = [...new Set(
        driverList
          .filter((d) => d.vehicleId != null)
          .map((d) => d.vehicleId!)
      )];

      // Fetch partition counts for each vehicle directly using the partition API
      const vehiclePartitionMap = new Map<number, number>();
      if (vehicleIds.length > 0) {
        const partitionResults = await Promise.all(
          vehicleIds.map(async (vId) => {
            try {
              const partitions = await transportApi.getVehiclePartitions(vId);
              return { vehicleId: vId, count: Array.isArray(partitions) ? partitions.length : 0 };
            } catch {
              return { vehicleId: vId, count: 0 };
            }
          })
        );
        for (const result of partitionResults) {
          vehiclePartitionMap.set(result.vehicleId, result.count);
        }
      }

      // Enrich drivers with partition count from their mapped vehicle
      const enrichedDrivers = driverList.map((driver) => ({
        ...driver,
        partitionCount: driver.vehicleId ? (vehiclePartitionMap.get(driver.vehicleId) ?? 0) : 0
      }));

      setDrivers(enrichedDrivers);
    } catch (err) {
      console.error('Failed to fetch drivers:', err);
      setDrivers([]);
    } finally {
      setDriversLoading(false);
    }
  }, []);

  const availableDrivers = useMemo(
    () => drivers.filter((d) => d.isAvailable !== false),
    [drivers]
  );

  const filteredDrivers = useMemo(() => {
    if (!driverSearchQuery.trim()) return availableDrivers;
    const query = driverSearchQuery.toLowerCase();
    return availableDrivers.filter(
      (d) =>
        d.name?.toLowerCase().includes(query) ||
        String(d.id).includes(query) ||
        d.employeeCode?.toLowerCase().includes(query) ||
        d.phoneNumber?.includes(query)
    );
  }, [availableDrivers, driverSearchQuery]);

  const handleOpenDriverDialog = () => {
    setSelectedDriver(null);
    setDriverSearchQuery('');
    setDriverValidationError(null);
    setIsDriverDialogOpen(true);
    fetchDrivers();
  };

  const handleDriverSelect = (driver: DriverDTO) => {
    setSelectedDriver(driver);
    setDriverValidationError(null);
  };

  const handleConfirmAssignDriver = async () => {
    if (!selectedDriver) {
      setDriverValidationError('Please select a driver before confirming.');
      return;
    }
    if (!route) return;

    try {
      setAssigningDriver(true);
      const updatedRoute = await transportApi.assignDriver(route.id!, selectedDriver.id);
      setRoute(updatedRoute);
      setError(null);
      setIsDriverDialogOpen(false);
    } catch (err) {
      setDriverValidationError(
        err instanceof Error ? err.message : 'Failed to assign driver'
      );
    } finally {
      setAssigningDriver(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!route) return;

    try {
      setActionLoading(true);
      let updatedRoute;

      switch (newStatus) {
        case 'ACTIVE':
          updatedRoute = await transportApi.activateRoute(route.id!);
          break;
        case 'RESUMED':
          updatedRoute = await transportApi.resumeRoute(route.id!);
          break;
        case 'PAUSED':
          updatedRoute = await transportApi.pauseRoute(route.id!);
          break;
        case 'CANCELLED':
          updatedRoute = await transportApi.cancelRoute(route.id!);
          break;
        default:
          return;
      }

      setRoute(updatedRoute);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update route');
    } finally {
      setActionLoading(false);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!route) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-sm text-red-800">Route not found</p>
      </div>
    );
  }

  const statusColor = statusColors[route.status || 'DRAFT'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/transport/raw-material/routes')}
            className="rounded-lg p-2 hover:bg-gray-100"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{route.routeName}</h1>
            <p className="text-sm text-gray-600">{route.routeCode}</p>
          </div>
        </div>
        <span className={`inline-flex items-center rounded-full ${statusColor.bg} ${statusColor.text} px-3 py-1 text-sm font-medium`}>
          {route.statusName || route.status || 'DRAFT'}
        </span>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Route Details Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Manufacturing Unit */}
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-xs font-medium text-gray-600 uppercase">Drop-off Point</p>
          <p className="mt-2 font-medium text-gray-900">{route.mfgUnitName}</p>
          <p className="text-sm text-gray-600">ID: {route.manufacturingUnitId}</p>
        </div>

        {/* Distance */}
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-xs font-medium text-gray-600 uppercase">Total Distance</p>
          <p className="mt-2 text-2xl font-bold text-gray-900">
            {route.totalDistanceKm?.toFixed(1) || '-'}
            <span className="text-lg text-gray-600 ml-1">km</span>
          </p>
        </div>

        {/* Estimated Duration */}
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-xs font-medium text-gray-600 uppercase">Est. Duration</p>
          <p className="mt-2 text-2xl font-bold text-gray-900">
            {route.estimatedDurationMin || '-'}
            <span className="text-lg text-gray-600 ml-1">min</span>
          </p>
        </div>

        {/* Stops Count */}
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-xs font-medium text-gray-600 uppercase">Stops</p>
          <p className="mt-2 text-2xl font-bold text-gray-900">{route.stops?.length || 0}</p>
        </div>
      </div>

      {/* Schedule Info */}
      {(route.routeStartTime || route.scheduleTypeName || route.scheduleConfig) && (
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Schedule Information</h3>
          <div className="grid gap-4 sm:grid-cols-3">
            {route.scheduleTypeName && (
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase">Schedule Type</p>
                <p className="mt-1 font-medium text-gray-900">{route.scheduleTypeName}</p>
              </div>
            )}
            {route.routeStartTime && (
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase">Start Time</p>
                <p className="mt-1 text-lg font-bold text-blue-600">
                  {(() => {
                    try {
                      const [h, m] = route.routeStartTime.split(':');
                      const hour = parseInt(h);
                      const ampm = hour >= 12 ? 'PM' : 'AM';
                      const displayHour = hour % 12 || 12;
                      return `${displayHour}:${m} ${ampm}`;
                    } catch {
                      return route.routeStartTime;
                    }
                  })()}
                </p>
              </div>
            )}
            {/* Display schedule date if set */}
            {route.scheduleConfig && (() => {
              try {
                // Handle both string and object formats for scheduleConfig
                const config = typeof route.scheduleConfig === 'string'
                  ? JSON.parse(route.scheduleConfig)
                  : route.scheduleConfig;
                if (config && config.date) {
                  return (
                    <div>
                      <p className="text-xs font-medium text-gray-600 uppercase">Schedule Date</p>
                      <p className="mt-1 text-lg font-bold text-blue-600">
                        {new Date(config.date).toLocaleDateString('en-IN', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  );
                }
              } catch {
                return null;
              }
              return null;
            })()}
          </div>
        </div>
      )}

      {/* Driver & Vehicle Assignment */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Driver & Vehicle Assignment</h3>
        {route.assignedDriverId ? (
          <div className="grid gap-4 sm:grid-cols-2">
            {/* Assigned Driver Card */}
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-600">Assigned Driver</p>
                <span className="inline-flex items-center gap-1 rounded-full bg-green-100 text-green-700 px-2 py-0.5 text-xs font-medium">
                  <CheckCircle2 size={12} />
                  Assigned
                </span>
              </div>
              <p className="text-lg font-semibold text-gray-900">{route.assignedDriverName}</p>
              <p className="text-sm text-gray-600">Driver ID: {route.assignedDriverId}</p>
              {route.status === 'DRAFT' && (
                <button
                  onClick={handleOpenDriverDialog}
                  disabled={actionLoading}
                  className="mt-3 w-full rounded-lg border border-blue-300 bg-white px-3 py-2 text-sm text-blue-700 hover:bg-blue-50 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                >
                  <UserPlus size={16} />
                  {actionLoading ? 'Updating...' : 'Reassign Driver'}
                </button>
              )}
            </div>
            {/* Assigned Vehicle Card */}
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-600">Mapped Vehicle</p>
                {route.assignedVehicleId && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-100 text-green-700 px-2 py-0.5 text-xs font-medium">
                    <CheckCircle2 size={12} />
                    Assigned
                  </span>
                )}
              </div>
              <p className="text-lg font-semibold text-gray-900">
                {route.assignedVehicleNumber || 'No vehicle mapped'}
              </p>
              {route.assignedVehicleId && (
                <p className="text-sm text-gray-600">Vehicle ID: {route.assignedVehicleId}</p>
              )}
              <p className="mt-3 text-xs text-gray-500 flex items-center gap-1">
                <Truck size={12} />
                Auto-assigned from driver's mapped vehicle
              </p>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {/* No Driver Assigned Card */}
            <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4">
              <p className="text-sm font-medium text-gray-600 mb-2">Assigned Driver</p>
              <p className="text-lg font-medium text-gray-400">Not Assigned</p>
              <button
                onClick={handleOpenDriverDialog}
                disabled={actionLoading}
                className="mt-3 w-full rounded-lg bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                <UserPlus size={16} />
                {actionLoading ? 'Updating...' : 'Assign Driver'}
              </button>
            </div>
            {/* No Vehicle Card */}
            <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4">
              <p className="text-sm font-medium text-gray-600 mb-2">Mapped Vehicle</p>
              <p className="text-lg font-medium text-gray-400">No vehicle mapped</p>
              <p className="mt-3 text-xs text-gray-500 flex items-center gap-1">
                <Truck size={12} />
                Vehicle will be auto-assigned when a driver is assigned
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Today's Execution Status - Real-time Driver Activity */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-600" />
            Today's Execution Status
          </h3>
          <button
            onClick={fetchTodayExecution}
            disabled={executionLoading}
            className="flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${executionLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {executionLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            <span className="ml-2 text-sm text-gray-600">Loading execution status...</span>
          </div>
        ) : !todayExecution ? (
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-center">
            <Clock className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
            <p className="text-sm text-yellow-800 font-medium">No execution scheduled for today</p>
            <p className="text-xs text-yellow-600 mt-1">Driver has not started this route today</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Execution Summary */}
            <div className="grid gap-4 sm:grid-cols-4">
              <div className="rounded-lg bg-gray-50 p-3 text-center">
                <p className="text-xs text-gray-600 uppercase font-medium">Status</p>
                <p className={`text-sm font-bold mt-1 ${
                  todayExecution.statusCode === 'COMPLETED' ? 'text-green-600' :
                  todayExecution.statusCode === 'STARTED' || todayExecution.statusCode === 'IN_PROGRESS' ? 'text-blue-600' :
                  'text-gray-600'
                }`}>
                  {todayExecution.statusName || todayExecution.statusCode || 'SCHEDULED'}
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 p-3 text-center">
                <p className="text-xs text-gray-600 uppercase font-medium">Completed</p>
                <p className="text-sm font-bold text-green-600 mt-1">
                  {stopLogs.filter(s => s.statusCode === 'COMPLETED').length} / {stopLogs.length}
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 p-3 text-center">
                <p className="text-xs text-gray-600 uppercase font-medium">Skipped</p>
                <p className="text-sm font-bold text-orange-600 mt-1">
                  {stopLogs.filter(s => s.statusCode === 'SKIPPED').length}
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 p-3 text-center">
                <p className="text-xs text-gray-600 uppercase font-medium">Total Collected</p>
                <p className="text-sm font-bold text-blue-600 mt-1">
                  {stopLogs.reduce((sum, s) => sum + (s.quantityCollectedKg || 0), 0).toFixed(1)} kg
                </p>
              </div>
            </div>

          </div>
        )}
      </div>

      {/* Gaushala Stops (Pickup Points) - with integrated driver progress */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Gaushala Stops (Pickup Points)</h3>
        {route.stops && route.stops.length > 0 ? (
          <div className="space-y-3">
            {route.stops.map((stop) => {
              // Find the corresponding stop log for this gaushala
              const stopLog = stopLogs.find(log => log.gaushalaId === stop.gaushalaId || log.stopId === stop.id);
              const hasExecution = todayExecution !== null;

              // Determine border and background colors based on stop status
              const stopStatus = stopLog?.statusCode || (hasExecution ? 'PENDING' : null);
              const borderColor =
                stopStatus === 'COMPLETED' ? 'border-l-green-500 border-green-200 bg-green-50/50' :
                stopStatus === 'ARRIVED' ? 'border-l-blue-500 border-blue-200 bg-blue-50/50' :
                stopStatus === 'SKIPPED' ? 'border-l-orange-500 border-orange-200 bg-orange-50/50' :
                stopStatus === 'PENDING' ? 'border-l-gray-400 border-gray-200' :
                'border-gray-200';

              const circleColor =
                stopStatus === 'COMPLETED' ? 'bg-green-500 text-white' :
                stopStatus === 'ARRIVED' ? 'bg-blue-500 text-white' :
                stopStatus === 'SKIPPED' ? 'bg-orange-500 text-white' :
                'bg-blue-100 text-blue-600';

              const statusBadgeColor =
                stopStatus === 'COMPLETED' ? 'bg-green-100 text-green-800 border-green-200' :
                stopStatus === 'ARRIVED' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                stopStatus === 'SKIPPED' ? 'bg-orange-100 text-orange-800 border-orange-200' :
                stopStatus === 'PENDING' ? 'bg-gray-100 text-gray-600 border-gray-200' :
                '';

              return (
                <div key={stop.id} className={`flex items-start gap-4 rounded-lg border border-l-4 p-4 transition-colors ${borderColor}`}>
                  <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full font-medium ${circleColor}`}>
                    {stopStatus === 'COMPLETED' ? <CheckCircle2 className="h-5 w-5" /> :
                     stopStatus === 'ARRIVED' ? <MapPin className="h-5 w-5" /> :
                     stopStatus === 'SKIPPED' ? <SkipForward className="h-5 w-5" /> :
                     stop.sequenceOrder}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900">{stop.gaushalaName}</p>
                        <p className="text-sm text-gray-600">ID: {stop.gaushalaId}</p>
                        {stop.gaushalaAddress && (
                          <p className="text-sm text-gray-600">{stop.gaushalaAddress}</p>
                        )}
                        {stop.expectedQuantityMinKg && stop.expectedQuantityMaxKg && (
                          <p className="text-sm text-gray-600">
                            Expected: {stop.expectedQuantityMinKg} - {stop.expectedQuantityMaxKg} kg
                          </p>
                        )}
                        {stop.rawMaterialTypeName && (
                          <p className="text-sm text-gray-600">
                            Raw Material: {stop.rawMaterialTypeName}
                          </p>
                        )}
                        {/* Display estimated arrival time */}
                        {(() => {
                          const timeToFormat = stop.estimatedArrivalTime ||
                            (stop.sequenceOrder === 1 && route.routeStartTime ? route.routeStartTime : null);
                          if (!timeToFormat) return null;
                          try {
                            const timeParts = timeToFormat.split(':');
                            const hour = parseInt(timeParts[0]);
                            const minute = timeParts[1].replace(/[^\d]/g, '').substring(0, 2).padStart(2, '0');
                            const ampm = hour >= 12 ? 'pm' : 'am';
                            const displayHour = hour % 12 || 12;
                            return (
                              <p className="text-sm text-gray-500">
                                Est. Arrival: {displayHour.toString().padStart(2, '0')}:{minute} {ampm}
                              </p>
                            );
                          } catch {
                            return (
                              <p className="text-sm text-gray-500">
                                Est. Arrival: {timeToFormat}
                              </p>
                            );
                          }
                        })()}
                      </div>

                      {/* Driver Progress Status - Right Side */}
                      {hasExecution && stopStatus && (
                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                          {/* Partition Badge */}
                          {stopLog?.partitionLabel && (
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-300 bg-blue-600 text-white px-3 py-1 text-xs font-medium">
                              <Truck className="h-3.5 w-3.5" />
                              {stopLog.partitionLabel}
                            </span>
                          )}
                          {/* Status Badge */}
                          <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${statusBadgeColor}`}>
                            {stopStatus === 'COMPLETED' && <CheckCircle2 className="h-3.5 w-3.5" />}
                            {stopStatus === 'ARRIVED' && <MapPin className="h-3.5 w-3.5" />}
                            {stopStatus === 'SKIPPED' && <SkipForward className="h-3.5 w-3.5" />}
                            {stopStatus === 'PENDING' && <Clock className="h-3.5 w-3.5" />}
                            {stopStatus}
                          </span>

                          {/* Collected Quantity */}
                          {stopLog?.statusCode === 'COMPLETED' && stopLog.quantityCollectedKg && (
                            <div className="flex items-center gap-1.5 text-green-700 bg-green-100 border border-green-200 px-2.5 py-1 rounded-lg">
                              <Package className="h-4 w-4" />
                              <span className="text-sm font-semibold">{stopLog.quantityCollectedKg.toFixed(1)} kg</span>
                            </div>
                          )}

                          {/* Skip Reason */}
                          {stopLog?.statusCode === 'SKIPPED' && stopLog.skipReason && (
                            <div className="text-xs text-orange-600 bg-orange-50 border border-orange-200 px-2 py-1 rounded max-w-[180px]" title={stopLog.skipReason}>
                              <span className="font-medium">Reason:</span> {stopLog.skipReason}
                            </div>
                          )}

                          {/* Arrival Time */}
                          {stopLog?.arrivedAt && (
                            <p className="text-xs text-gray-500">
                              Arrived: {new Date(stopLog.arrivedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-600">No stops configured</p>
        )}
      </div>

      {/* Actions */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Route Actions</h3>
        <div className="flex flex-wrap gap-3">
          {route.status === 'DRAFT' && (
            <>
              <button
                onClick={() => handleStatusChange('ACTIVE')}
                disabled={actionLoading || !route.assignedDriverId || !route.assignedVehicleId || !route.stops || route.stops.length === 0}
                className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                <Play size={18} />
                Activate Route
              </button>
              {(!route.assignedDriverId || !route.stops || route.stops.length === 0) && (
                <p className="text-sm text-gray-600">Assign a driver (with mapped vehicle) and add stops to activate</p>
              )}
              {route.assignedDriverId && !route.assignedVehicleId && (
                <p className="text-sm text-amber-600">Selected driver has no mapped vehicle. Please assign a vehicle to the driver first.</p>
              )}
            </>
          )}

          {route.status === 'ACTIVE' && (
            <button
              onClick={() => handleStatusChange('PAUSED')}
              disabled={actionLoading}
              className="flex items-center gap-2 rounded-lg bg-yellow-600 px-4 py-2 text-white hover:bg-yellow-700 disabled:opacity-50 transition-colors"
            >
              <Pause size={18} />
              Pause Route
            </button>
          )}

          {route.status === 'PAUSED' && (
            <button
              onClick={() => handleStatusChange('RESUMED')}
              disabled={actionLoading}
              className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              <Play size={18} />
              Resume Route
            </button>
          )}

          {(route.status === 'ACTIVE' || route.status === 'PAUSED' || route.status === 'DRAFT') && (
            <button
              onClick={() => {
                if (confirm('Are you sure you want to cancel this route?')) {
                  handleStatusChange('CANCELLED');
                }
              }}
              disabled={actionLoading}
              className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              <X size={18} />
              Cancel Route
            </button>
          )}
        </div>
      </div>

      {/* Assign Driver Dialog */}
      <Dialog open={isDriverDialogOpen} onOpenChange={setIsDriverDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Assign Driver
            </DialogTitle>
            <DialogDescription>
              Select a driver to assign to this route. The driver's mapped vehicle will be auto-assigned.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            {driversLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                <span className="ml-2 text-sm text-gray-600">Loading drivers...</span>
              </div>
            ) : availableDrivers.length === 0 ? (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-center">
                <p className="text-sm text-yellow-800 font-medium">No drivers available</p>
                <p className="text-xs text-yellow-600 mt-1">
                  All drivers are currently unavailable or none have been added yet.
                </p>
              </div>
            ) : (
              <>
                {/* Selected Driver Preview */}
                {selectedDriver && (
                  <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-blue-900">{selectedDriver.name}</p>
                        <p className="text-xs text-blue-700">
                          ID: {selectedDriver.id}
                          {selectedDriver.phoneNumber && ` | ${selectedDriver.phoneNumber}`}
                        </p>
                      </div>
                      <Check className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="mt-2 pt-2 border-t border-blue-200 flex items-center justify-between">
                      <p className="text-xs font-medium text-blue-800 flex items-center gap-1">
                        <Truck size={12} />
                        Mapped Vehicle: {selectedDriver.vehicleRegistration || 'No vehicle mapped'}
                      </p>
                      {selectedDriver.vehicleRegistration && (
                        <span className="text-xs font-medium text-blue-700 bg-blue-100 rounded px-1.5 py-0.5">
                          {selectedDriver.partitionCount ?? 0} {(selectedDriver.partitionCount ?? 0) === 1 ? 'Partition' : 'Partitions'}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Search Input */}
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name or ID..."
                    value={driverSearchQuery}
                    onChange={(e) => setDriverSearchQuery(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    aria-label="Search drivers"
                  />
                </div>

                {/* Driver List */}
                <div className="max-h-[220px] overflow-y-auto rounded-lg border border-gray-200">
                  {filteredDrivers.length === 0 ? (
                    <p className="py-6 text-center text-sm text-gray-500">No drivers match your search.</p>
                  ) : (
                    filteredDrivers.map((driver) => {
                      const isSelected = selectedDriver?.id === driver.id;
                      return (
                        <button
                          key={driver.id}
                          type="button"
                          onClick={() => handleDriverSelect(driver)}
                          className={`w-full text-left px-3 py-2.5 flex items-center justify-between border-b border-gray-100 last:border-b-0 transition-colors cursor-pointer ${
                            isSelected
                              ? 'bg-blue-50 hover:bg-blue-100'
                              : 'hover:bg-gray-50'
                          }`}
                          aria-label={`Select driver ${driver.name}`}
                          tabIndex={0}
                        >
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                              {driver.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              ID: {driver.id}
                              {driver.employeeCode && ` | ${driver.employeeCode}`}
                              {driver.phoneNumber && ` | ${driver.phoneNumber}`}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                            {driver.vehicleRegistration ? (
                              <div className="flex items-center gap-1.5">
                                <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600 flex items-center gap-1">
                                  <Truck size={10} />
                                  {driver.vehicleRegistration}
                                </span>
                                <span className="rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-600 font-medium">
                                  {driver.partitionCount ?? 0}P
                                </span>
                              </div>
                            ) : (
                              <span className="text-xs text-amber-500">No vehicle</span>
                            )}
                            {isSelected && (
                              <Check className="h-4 w-4 text-blue-600" />
                            )}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </>
            )}

            {/* Validation Error */}
            {driverValidationError && (
              <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-800">{driverValidationError}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => setIsDriverDialogOpen(false)}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmAssignDriver}
              disabled={!selectedDriver || assigningDriver || driversLoading}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {assigningDriver ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Assigning...
                </>
              ) : (
                'Assign Driver'
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
